// SPDX-FileCopyrightText: Copyright (c) 2025-2026 provide.io llc. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
/**
 * TelemetryConfig — mirrors Python provide.telemetry TelemetryConfig.
 *
 * Env vars (same names as Python package):
 *   PROVIDE_TELEMETRY_SERVICE_NAME, PROVIDE_TELEMETRY_ENV (fallback: PROVIDE_ENV),
 *   PROVIDE_TELEMETRY_VERSION (fallback: PROVIDE_VERSION),
 *   PROVIDE_LOG_LEVEL, PROVIDE_LOG_FORMAT, PROVIDE_TRACE_ENABLED,
 *   PROVIDE_TELEMETRY_STRICT_SCHEMA,
 *   OTEL_EXPORTER_OTLP_ENDPOINT, OTEL_EXPORTER_OTLP_HEADERS
 */
import { setSamplingPolicy } from './sampling';
import { setQueuePolicy } from './backpressure';
import { setExporterPolicy } from './resilience';
import { ConfigurationError } from './exceptions';
import { setSetupError } from './health';
import { awaitPropagationInit, isFallbackMode, isPropagationInitDone } from './propagation';
import { _setActiveConfig } from './runtime';
import { configFromEnv } from './config-env';
export { configFromEnv } from './config-env';
export const DEFAULTS = {
    serviceName: 'provide-service',
    environment: 'dev',
    version: '0.0.0',
    logLevel: 'info',
    logFormat: 'console',
    otelEnabled: true,
    otlpLogsEnabled: true,
    sanitizeFields: [],
    captureToWindow: true,
    consoleOutput: true,
    strictSchema: false,
    strictEventName: false,
    requiredLogKeys: [],
    logIncludeTimestamp: true,
    logIncludeCaller: true,
    logSanitize: true,
    logCodeAttributes: false,
    logModuleLevels: {},
    logPrettyKeyColor: 'dim',
    logPrettyValueColor: '',
    logPrettyFields: [],
    traceSampleRate: 1.0,
    tracingEnabled: true,
    metricsEnabled: true,
    samplingLogsRate: 1.0,
    samplingTracesRate: 1.0,
    samplingMetricsRate: 1.0,
    backpressureLogsMaxsize: 0,
    backpressureTracesMaxsize: 0,
    backpressureMetricsMaxsize: 0,
    exporterLogsRetries: 0,
    exporterLogsBackoffMs: 0,
    exporterLogsTimeoutMs: 10000,
    exporterLogsShutdownTimeoutMs: 5000,
    exporterLogsFailOpen: true,
    exporterTracesRetries: 0,
    exporterTracesBackoffMs: 0,
    exporterTracesTimeoutMs: 10000,
    exporterTracesFailOpen: true,
    exporterMetricsRetries: 0,
    exporterMetricsBackoffMs: 0,
    exporterMetricsTimeoutMs: 10000,
    exporterMetricsFailOpen: true,
    sloEnableRedMetrics: false,
    sloEnableUseMetrics: false,
    piiMaxDepth: 8,
    securityMaxAttrValueLength: 1024,
    securityMaxAttrCount: 64,
};
let _config = { ...DEFAULTS };
/** Incremented on every setupTelemetry() call so getRootLogger() knows to rebuild. */
let _configVersion = 0;
/** Return the current config version (used by logger to detect stale root). */
export function _getConfigVersion() {
    return _configVersion;
}
/** Return the active TelemetryConfig. */
export function getConfig() {
    return _config;
}
/**
 * Apply parsed config fields to the runtime policy engines (sampling, backpressure, resilience).
 * Mirrors Python provide.telemetry.runtime.apply_runtime_config.
 */
export function applyConfigPolicies(cfg) {
    // Sampling
    setSamplingPolicy('logs', { defaultRate: cfg.samplingLogsRate });
    setSamplingPolicy('traces', {
        defaultRate: Math.min(cfg.samplingTracesRate, cfg.traceSampleRate),
    });
    setSamplingPolicy('metrics', { defaultRate: cfg.samplingMetricsRate });
    // Backpressure
    setQueuePolicy({
        maxLogs: cfg.backpressureLogsMaxsize,
        maxTraces: cfg.backpressureTracesMaxsize,
        maxMetrics: cfg.backpressureMetricsMaxsize,
    });
    // Exporter resilience (per-signal)
    setExporterPolicy('logs', {
        retries: cfg.exporterLogsRetries,
        backoffMs: cfg.exporterLogsBackoffMs,
        timeoutMs: cfg.exporterLogsTimeoutMs,
        failOpen: cfg.exporterLogsFailOpen,
    });
    setExporterPolicy('traces', {
        retries: cfg.exporterTracesRetries,
        backoffMs: cfg.exporterTracesBackoffMs,
        timeoutMs: cfg.exporterTracesTimeoutMs,
        failOpen: cfg.exporterTracesFailOpen,
    });
    setExporterPolicy('metrics', {
        retries: cfg.exporterMetricsRetries,
        backoffMs: cfg.exporterMetricsBackoffMs,
        timeoutMs: cfg.exporterMetricsTimeoutMs,
        failOpen: cfg.exporterMetricsFailOpen,
    });
}
const _FALLBACK_MESSAGE = 'AsyncLocalStorage unavailable in a Node.js environment — concurrent requests would share propagation context. Check that node:async_hooks is not excluded from your bundler config.';
function _isNodeLike() {
    return (typeof process !== 'undefined' &&
        typeof process.versions === 'object' &&
        typeof process.versions.node === 'string');
}
function _applySetupBody(overrides) {
    _config = { ...configFromEnv(), ...overrides };
    _validateConfig(_config);
    _configVersion++;
    _setActiveConfig(_config);
    try {
        applyConfigPolicies(_config);
    }
    catch (err) {
        const message = err instanceof Error ? err.message : String(err);
        setSetupError(message);
        console.warn(`setupTelemetry: applyConfigPolicies failed: ${message}`);
    }
}
/**
 * Configure telemetry. Merges explicit values over env-derived defaults.
 * Best-effort ALS check: defers a warning when init is still racing.
 * Prefer `setupTelemetryAsync` from ESM entry points that need a hard
 * "safe or throws" guarantee.
 */
export function setupTelemetry(overrides) {
    const isNode = _isNodeLike();
    if (isNode && isFallbackMode()) {
        if (isPropagationInitDone()) {
            // Init has settled and ALS is genuinely unavailable — fail loud as before.
            throw new ConfigurationError(_FALLBACK_MESSAGE);
        }
        // Init still racing (typical of tsx/ESM Node where propagation.ts loads
        // node:async_hooks via fire-and-forget `await import`). Defer the check
        // to after init resolves; record + warn instead of throwing because the
        // call site has already returned by the time we know the verdict.
        void awaitPropagationInit().then(() => {
            if (isFallbackMode()) {
                setSetupError(_FALLBACK_MESSAGE);
                console.warn(`[provide-telemetry] ${_FALLBACK_MESSAGE}`);
            }
        });
    }
    _applySetupBody(overrides);
}
/**
 * Async variant of `setupTelemetry` that awaits AsyncLocalStorage init
 * before returning. Use this from ESM entry points (e.g. servers starting
 * at module scope) so you get an "either safe or throws" contract rather
 * than the best-effort deferred warning of the sync variant. Throws
 * `ConfigurationError` when ALS is genuinely unavailable on a Node runtime.
 */
export async function setupTelemetryAsync(overrides) {
    _applySetupBody(overrides);
    if (!_isNodeLike())
        return;
    await awaitPropagationInit();
    if (isFallbackMode()) {
        setSetupError(_FALLBACK_MESSAGE);
        throw new ConfigurationError(_FALLBACK_MESSAGE);
    }
}
/** Validate config values — reject out-of-range instead of silently clamping (fail-fast contract). */
function _validateConfig(cfg) {
    const requireRate = (name, v) => {
        if (!Number.isFinite(v) || v < 0 || v > 1) {
            throw new ConfigurationError(`${name} must be in [0, 1], got ${String(v)}`);
        }
    };
    const requireNonNegInt = (name, v) => {
        if (!Number.isInteger(v) || v < 0) {
            throw new ConfigurationError(`${name} must be a non-negative integer, got ${String(v)}`);
        }
    };
    requireRate('samplingLogsRate', cfg.samplingLogsRate);
    requireRate('samplingTracesRate', cfg.samplingTracesRate);
    requireRate('samplingMetricsRate', cfg.samplingMetricsRate);
    requireRate('traceSampleRate', cfg.traceSampleRate);
    requireNonNegInt('backpressureLogsMaxsize', cfg.backpressureLogsMaxsize);
    requireNonNegInt('backpressureTracesMaxsize', cfg.backpressureTracesMaxsize);
    requireNonNegInt('backpressureMetricsMaxsize', cfg.backpressureMetricsMaxsize);
    requireNonNegInt('exporterLogsRetries', cfg.exporterLogsRetries);
    requireNonNegInt('exporterTracesRetries', cfg.exporterTracesRetries);
    requireNonNegInt('exporterMetricsRetries', cfg.exporterMetricsRetries);
    requireNonNegInt('securityMaxAttrValueLength', cfg.securityMaxAttrValueLength);
    requireNonNegInt('securityMaxAttrCount', cfg.securityMaxAttrCount);
    requireNonNegInt('piiMaxDepth', cfg.piiMaxDepth);
}
/** Reset to defaults (used in tests). */
export function _resetConfig() {
    _config = { ...DEFAULTS };
    _configVersion = 0;
}
/**
 * Parse OTLP-style header string "key=value,key2=value2" into a Record.
 * Keys and values are URL-decoded. Malformed pairs (no '=') and empty keys are skipped.
 * Values may contain '=' characters (only the first '=' splits key from value).
 */
export function parseOtlpHeaders(raw) {
    const result = {};
    // Stryker disable next-line ConditionalExpression: early return is an optimization — empty string splits to [""], idx<1 skips the only pair, returns {} identically
    if (!raw)
        return result;
    for (const pair of raw.split(',')) {
        const idx = pair.indexOf('=');
        if (idx < 1)
            continue; // no '=' or empty key
        const rawKey = pair.slice(0, idx).trim();
        const rawVal = pair.slice(idx + 1).trim();
        try {
            const key = decodeURIComponent(rawKey);
            // Stryker disable next-line ConditionalExpression: defensive guard — unreachable because idx<1 check and trim() already exclude empty keys
            /* v8 ignore next: defensive — idx<1 and trim() already exclude observable empty keys */
            if (!key)
                continue;
            const val = decodeURIComponent(rawVal);
            result[key] = val;
        }
        catch {
            // Stryker disable next-line BlockStatement: `continue` in for..of catch is equivalent to an empty body — next iteration starts either way
            continue;
        }
    }
    return result;
}
/** Mask a single header value: show first 4 chars + **** if >= 8 chars, else ****. */
function maskHeaderValue(v) {
    return v.length < 8 ? '****' : v.slice(0, 4) + '****';
}
/** Mask the password component of a URL's userinfo, if present. */
function maskEndpointUrl(raw) {
    try {
        const u = new URL(raw);
        if (u.password) {
            u.password = '****';
            return u.toString();
        }
    }
    catch {
        /* not a valid URL — return as-is */
    }
    return raw;
}
/**
 * Return a copy of the config with OTLP secrets masked.
 * Safe to log or serialize — never leaks header values or endpoint credentials.
 */
export function redactConfig(config) {
    const result = { ...config };
    // Stryker disable next-line EqualityOperator,ConditionalExpression: empty headers produce {} from Object.fromEntries — equivalent; undefined headers throw on Object.keys but caught identically
    if (config.otlpHeaders && Object.keys(config.otlpHeaders).length > 0) {
        result.otlpHeaders = Object.fromEntries(Object.entries(config.otlpHeaders).map(([k, v]) => [k, maskHeaderValue(v)]));
    }
    // Mask per-signal headers
    for (const field of ['otlpLogsHeaders', 'otlpTracesHeaders', 'otlpMetricsHeaders']) {
        const hdrs = config[field];
        if (hdrs && Object.keys(hdrs).length > 0) {
            result[field] = Object.fromEntries(Object.entries(hdrs).map(([k, v]) => [k, maskHeaderValue(v)]));
        }
    }
    // Stryker disable next-line ConditionalExpression: maskEndpointUrl(undefined) returns undefined via catch — equivalent to skipping the block
    if (config.otlpEndpoint) {
        result.otlpEndpoint = maskEndpointUrl(config.otlpEndpoint);
    }
    // Mask per-signal endpoints
    for (const field of ['otlpLogsEndpoint', 'otlpTracesEndpoint', 'otlpMetricsEndpoint']) {
        if (config[field]) {
            result[field] = maskEndpointUrl(config[field]);
        }
    }
    return result;
}
/** Package version — mirrors Python __version__. */
export const version = '0.4.7';
export const __version__ = version;
